<template>
  <div class="interactive-schedule-viewer">
    <!-- Header with progress -->
    <div class="viewer-header">
      <h3>🔄 Live Schedule Generation</h3>
      <div v-if="isGenerating" class="progress-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
        <span class="progress-text">
          {{ currentSlotIndex + 1 }} / {{ totalSlots }} days
          ({{ Math.round(progress) }}%)
        </span>
      </div>
      <div v-else-if="isComplete" class="status-complete">
        ✅ Generation Complete!
      </div>
    </div>

    <!-- Live stats -->
    <div class="live-stats">
      <div class="stat-card">
        <div class="stat-label">Total Shifts</div>
        <div class="stat-value">{{ totalShiftsAssigned }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Real Workers</div>
        <div class="stat-value">{{ realWorkerShifts }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Dummy Workers</div>
        <div class="stat-value warning">{{ dummyWorkerShifts }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Avg Coverage</div>
        <div class="stat-value">{{ averageCoverage.toFixed(1) }}/{{ targetWorkers }}</div>
      </div>
    </div>

    <!-- Calendar grid -->
    <div class="calendar-grid">
      <div 
        v-for="(day, index) in days" 
        :key="day.date"
        class="day-card"
        :class="{
          'day-complete': day.isComplete,
          'day-active': day.isActive,
          'day-pending': !day.isComplete && !day.isActive,
          'day-understaffed': day.assignedCount < targetWorkers && day.isComplete
        }"
      >
        <!-- Date header -->
        <div class="day-header">
          <div class="day-date">{{ formatDate(day.date) }}</div>
          <div class="day-coverage">
            {{ day.assignedCount }} / {{ day.requiredCount }}
            <span v-if="day.isComplete" class="checkmark">✓</span>
            <span v-else-if="day.isActive" class="spinner">⟳</span>
          </div>
        </div>

        <!-- Assigned workers -->
        <div class="day-workers">
          <div 
            v-for="(shift, shiftIdx) in day.shifts" 
            :key="`${day.date}-${shiftIdx}`"
            class="worker-badge"
            :class="{ 'dummy-worker': shift.is_dummy }"
            :title="`${shift.employee_name} (${shift.hours}h)`"
          >
            <span class="worker-icon">{{ shift.is_dummy ? '🔶' : '👤' }}</span>
            <span class="worker-name">{{ shift.employee_name }}</span>
            <span class="worker-hours">{{ shift.hours }}h</span>
          </div>
          
          <!-- Empty slots -->
          <div 
            v-for="n in (day.requiredCount - day.assignedCount)" 
            :key="`empty-${n}`"
            class="worker-badge empty"
          >
            <span class="worker-icon">⭕</span>
            <span class="worker-name">Waiting...</span>
          </div>
        </div>

        <!-- Coverage bar -->
        <div class="coverage-bar">
          <div 
            class="coverage-fill"
            :class="{
              'full': day.assignedCount >= day.requiredCount,
              'partial': day.assignedCount > 0 && day.assignedCount < day.requiredCount
            }"
            :style="{ width: `${(day.assignedCount / day.requiredCount) * 100}%` }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Activity log -->
    <div class="activity-log">
      <h4>📋 Activity Log</h4>
      <div class="log-entries">
        <div 
          v-for="(entry, index) in recentLogs" 
          :key="index"
          class="log-entry"
          :class="`log-${entry.type}`"
        >
          <span class="log-time">{{ entry.time }}</span>
          <span class="log-message">{{ entry.message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  targetWorkers: {
    type: Number,
    required: true
  },
  startDate: {
    type: String,
    required: true
  },
  endDate: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['complete', 'error'])

// State
const isGenerating = ref(false)
const isComplete = ref(false)
const days = ref([])
const totalSlots = ref(0)
const currentSlotIndex = ref(-1)
const activityLog = ref([])
const eventSource = ref(null)

// Stats
const totalShiftsAssigned = computed(() => 
  days.value.reduce((sum, day) => sum + day.shifts.length, 0)
)

const realWorkerShifts = computed(() => 
  days.value.reduce((sum, day) => 
    sum + day.shifts.filter(s => !s.is_dummy).length, 0
  )
)

const dummyWorkerShifts = computed(() => 
  days.value.reduce((sum, day) => 
    sum + day.shifts.filter(s => s.is_dummy).length, 0
  )
)

const averageCoverage = computed(() => {
  const completeDays = days.value.filter(d => d.isComplete)
  if (completeDays.length === 0) return 0
  return completeDays.reduce((sum, day) => sum + day.assignedCount, 0) / completeDays.length
})

const progress = computed(() => {
  if (totalSlots.value === 0) return 0
  return ((currentSlotIndex.value + 1) / totalSlots.value) * 100
})

const recentLogs = computed(() => activityLog.value.slice(-15).reverse())

// Methods
const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr)
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`
  } catch (e) {
    return dateStr
  }
}

const parseDate = (dateStr) => {
  return new Date(dateStr + 'T00:00:00')
}

const formatDateISO = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const addLog = (type, message) => {
  activityLog.value.push({
    type,
    message,
    time: new Date().toLocaleTimeString()
  })
}

const initializeDays = (start, end, targetCount) => {
  const startDate = parseDate(start)
  const endDate = parseDate(end)
  const daysList = []
  
  let current = new Date(startDate)
  while (current <= endDate) {
    daysList.push({
      date: formatDateISO(current),
      requiredCount: targetCount,
      assignedCount: 0,
      shifts: [],
      isActive: false,
      isComplete: false
    })
    current.setDate(current.getDate() + 1)
  }
  
  days.value = daysList
}

const startGeneration = (sseUrl) => {
  isGenerating.value = true
  isComplete.value = false
  activityLog.value = []
  
  addLog('info', '🚀 Starting schedule generation...')
  
  eventSource.value = new EventSource(sseUrl)
  
  eventSource.value.onmessage = (event) => {
    try {
      const update = JSON.parse(event.data)
      handleUpdate(update)
    } catch (e) {
      console.error('Error parsing SSE message:', e)
    }
  }
  
  eventSource.value.onerror = (error) => {
    console.error('SSE error:', error)
    addLog('error', '❌ Connection error - retrying...')
    
    // Auto-close after multiple errors
    if (eventSource.value.readyState === EventSource.CLOSED) {
      stopGeneration()
      emit('error', 'Connection lost')
    }
  }
}

const handleUpdate = (update) => {
  switch (update.type) {
    case 'init':
      totalSlots.value = update.total_slots
      initializeDays(
        update.time_period.start, 
        update.time_period.end, 
        update.target_workers_per_slot
      )
      addLog('info', `📅 Initialized ${update.total_slots} days`)
      break
      
    case 'slot_start':
      currentSlotIndex.value = update.slot_index
      if (days.value[update.slot_index]) {
        days.value[update.slot_index].isActive = true
        addLog('info', `📍 Processing ${days.value[update.slot_index].date}`)
      }
      break
      
    case 'shift_assigned':
      const dayIdx = update.slot_index
      if (days.value[dayIdx]) {
        days.value[dayIdx].shifts.push(update.shift)
        days.value[dayIdx].assignedCount++
        
        const icon = update.shift.is_dummy ? '🔶' : '✅'
        addLog(
          update.shift.is_dummy ? 'warning' : 'success',
          `${icon} ${update.shift.employee_name} → ${days.value[dayIdx].date}`
        )
      }
      break
      
    case 'slot_complete':
      if (days.value[update.slot_index]) {
        days.value[update.slot_index].isActive = false
        days.value[update.slot_index].isComplete = true
        
        const coverage = update.coverage_percent.toFixed(0)
        const icon = coverage >= 100 ? '✅' : '⚠️'
        addLog(
          coverage >= 100 ? 'success' : 'warning',
          `${icon} Day complete: ${coverage}% coverage`
        )
      }
      break
      
    case 'complete':
      isGenerating.value = false
      isComplete.value = true
      stopGeneration()
      
      addLog('success', `🎉 Complete! ${update.summary.total_shifts} shifts generated`)
      addLog('info', `📊 Real workers: ${update.summary.total_shifts - update.summary.dummy_shifts}`)
      addLog('info', `🔶 Dummy shifts: ${update.summary.dummy_shifts}`)
      
      emit('complete', update.schedule)
      break
      
    case 'error':
      addLog('error', `❌ Error: ${update.message}`)
      stopGeneration()
      emit('error', update.message)
      break
  }
}

const stopGeneration = () => {
  if (eventSource.value) {
    eventSource.value.close()
    eventSource.value = null
  }
  isGenerating.value = false
}

// Expose methods to parent
defineExpose({
  startGeneration,
  stopGeneration
})
</script>

<style scoped>
.interactive-schedule-viewer {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.viewer-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.viewer-header h3 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.progress-bar {
  flex: 1;
  height: 30px;
  background: #e0e0e0;
  border-radius: 15px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  transition: width 0.3s ease;
}

.progress-text {
  font-weight: 600;
  color: #666;
  min-width: 150px;
}

.status-complete {
  font-size: 18px;
  color: #4caf50;
  font-weight: 600;
}

.live-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.stat-card {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.stat-value.warning {
  color: #ff9800;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.day-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.day-pending {
  opacity: 0.5;
}

.day-active {
  border: 3px solid #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  animation: pulse 1.5s infinite;
}

.day-complete {
  border: 2px solid #4caf50;
}

.day-understaffed {
  border: 2px solid #ff9800;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}

.day-date {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.day-coverage {
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
}

.checkmark {
  color: #4caf50;
  font-size: 16px;
}

.spinner {
  color: #2196f3;
  font-size: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.day-workers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 120px;
}

.worker-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #e3f2fd;
  border-radius: 6px;
  border-left: 4px solid #2196f3;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.worker-badge.dummy-worker {
  background: #fff3e0;
  border-left-color: #ff9800;
}

.worker-badge.empty {
  background: #f5f5f5;
  border-left-color: #ccc;
  opacity: 0.6;
}

.worker-icon {
  font-size: 16px;
}

.worker-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.worker-hours {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.coverage-bar {
  margin-top: 10px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.coverage-fill {
  height: 100%;
  background: #ccc;
  transition: width 0.3s ease;
}

.coverage-fill.partial {
  background: #ff9800;
}

.coverage-fill.full {
  background: #4caf50;
}

.activity-log {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.activity-log h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #333;
}

.log-entries {
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.log-entry {
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  gap: 10px;
}

.log-info {
  background: #e3f2fd;
  color: #1976d2;
}

.log-success {
  background: #e8f5e9;
  color: #388e3c;
}

.log-warning {
  background: #fff3e0;
  color: #f57c00;
}

.log-error {
  background: #ffebee;
  color: #c62828;
}

.log-time {
  font-weight: 600;
  opacity: 0.7;
}

.log-message {
  flex: 1;
}
</style>
